/**
 * This is where all models that are shared between client and server are defined
 * @module sharedModels
 */

const SharedConstants = require('./sharedConstants');

/** Achievement class */
exports.Achievement = class Achievement{
   /**
    * Update an Achievement (also used in creation)
    * @param {number} data.idAchievement - id of the Achievement
    * @param {string} data.name - name of the Achievement
    * @param {string} data.image - link to the image
    * @param {string} data.description - Achievement description text
    * @param {number} data.pointValue - how many points the Achievement is worth
    */
   update(data) {
      this.idAchievement = data.idAchievement;
      this.name = data.name;
      this.image = data.image;
      this.description = data.description;
      this.pointValue = data.pointValue;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/**
 * Helper function to take an array of unformatted achievement data and return a new array with the data formatted
 * @param achievementArray
 * @returns {Array<Achievement>}
 */
exports.formatAchievementData = function(achievementArray){
   let processed = [];
   for(let achievement of achievementArray){
      processed.push(new exports.Achievement(achievement));
   }
   return processed;
};

/** CardBase class */
class CardBase{
   /**
    * Update an EvidenceCard (also used in creation)
    * @param {string} data.synopsis - the main text of the card
    * @param {string} data.idUser - id of the user that created the card
    * @param {number} data.vote - The vote on this card by the current user
    * @param {string} data.title - Title (or header) of the card
    * @param {string} data.creatorFirstName
    * @param {string} data.creatorLastName
    * @param {number} data.creatorHasAvatar
    * @param {User} data.creator - Takes precedence over first and last name. It means the {@link User} object is already formatted
    * @param {string} data.creationTime
    * @param {Object[]} data.topics
    * @param {Object} data.votes
    * @param {number} data.weakVoteCount
    * @param {number} data.strongVoteCount
    * @param {Comment[]} data.comments
    * @param {boolean} data.isApproved
    * @param {boolean} data.isPrivate
    * @param {boolean} data.isDeleted
    * @param {boolean} data.isRejected
    * @param {boolean} data.needsRevision
    * @param {number} data.voteType - sharedConstants.VoteType
    * @param {number} data.idSubmitter - user who submitted the card (to a portfolio, etc)
    * @param {number} data.submitterFirstName
    * @param {number} data.submitterLastName
    * @param {object} data.approvals
    * @param {string[]} data.tags
    * @param {string[]} data.workspaceTags
    * @param {number} data.idx - which row number in the portfolio the card is
    * @param {number} data.idUpvoteForCurrentUser - the id of the upvote if the user has upvoted it, otherwise 0
    * @param {number} data.upvoteCount
    */
   update(data = {}) {
      this.synopsis = data.synopsis || 'none';
      this.idUser = data.idUser;
      this.vote = data.vote;
      this.title = data.title;
      this.creationTime = data.creationTime;
      this.isApproved = data.isApproved;
      this.isPrivate = data.isPrivate;
      this.isDeleted = data.isDeleted;
      this.isRejected = data.isRejected;
      this.needsRevision = data.needsRevision;
      this.voteType = data.voteType;
      this.idSubmitter = data.idSubmitter;
      this.submitterFirstName = data.submitterFirstName;
      this.submitterLastName = data.submitterLastName;
      this.idUpvoteForCurrentUser = data.idUpvoteForCurrentUser;
      this.upvoteCount = data.upvoteCount

      if(data.creator){
         this.creator = new exports.User(data.creator);
      }
      else{
         this.creator = new exports.User({
            firstName: data.creatorFirstName,
            lastName: data.creatorLastName,
            hasAvatar: data.creatorHasAvatar,
            id: data.idUser,
            points:data.creatorPoints,
            affiliation: data.creatorAffiliation,
            bio: data.creatorBio,

         })
      }
      this.topics = [];
      if(data.topics){
         for(let topic of data.topics){
            this.topics.push(new exports.Topic(topic));
         }
      }
      this.tags = [];
      if(data.tags){
         for(let tag of data.tags){
            this.tags.push(new exports.Tag(tag));
         }
      }
      if(data.votes){
         this.votes = data.votes;
      }
      else{
         this.votes={
            weakVoteCount: (data.weakVoteCount ? data.weakVoteCount : 0),
            strongVoteCount: (data.strongVoteCount ? data.strongVoteCount : 0),
         }
      }

      if(data.comments){
         this.comments = data.comments;
      }
      else{
         this.comments = [];
      }

      if(data.approvals){
         this.approvals = data.approvals;
      }
      else{
         this.approvals = [];
      }

      if(data.workspaceTags){
         this.workspaceTags = data.workspaceTags;
      }
      else{
         this.workspaceTags = [];
      }

      this.index = data.idx || data.index;


   }

   /**
    * constructor just calls the update function
    * @param {Object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
}

/** EvidenceCard class */
exports.EvidenceCard = class EvidenceCard extends CardBase{
   /**
    * Update an EvidenceCard (also used in creation)
    * @param {number} data.idEvidenceCard - id of the EvidenceCard
    * @param {string} data.source - Evidence source (something like Wikipedia, etc.)
    * @param {string} data.citation - MLA format or similar
    * @param {string} data.sourceDate
    * @param {GreySpaceCard[]} data.greySpaceCards
    */
   update(data = {}) {
      super.update(data);
      this.idEvidenceCard = data.idEvidenceCard;
      this.id = data.idEvidenceCard || data.id;
      this.source = data.source;
      this.citation = data.citation;
      this.sourceDate = data.sourceDate;

      if(data.greySpaceCards){
         this.greySpaceCards = data.greySpaceCards;
      }
      else{
         this.greySpaceCards = [];
      }

      if(data.arpahEntry){
         this.arpahEntry = data.arpahEntry;
      }

   }

   /**
    * constructor just calls the update function
    * @param {Object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      super(data);
      this.cardType = SharedConstants.CardType.Evidence;
   }

   hasValidationErrors(){
      let bHasValidationErrors = false;
      if(this.topics.length === 0){
         bHasValidationErrors = true;
      }
      if(!this.title || this.title.trim() === '' ){
         bHasValidationErrors = true;
      }
      if(!this.synopsis || this.synopsis.trim() === '' ){
         bHasValidationErrors = true;
      }
      if(!this.source || this.source.trim() === '' ){
         bHasValidationErrors = true;
      }
      return bHasValidationErrors;
   }
};

exports.formatEvidenceData  = function(array){
   let processed = [];
   for(let obj of array){
      processed.push(new exports.EvidenceCard(obj));
   }
   return processed;
};

/** GreySpaceCard class */
exports.GreySpaceCard = class GreySpaceCard extends CardBase{
   /**
    * Update a GreySpaceCard (also used in creation)
    * @param {number} data.idGreySpaceCard - id of the GreySpaceCard
    * @param {number} data.idUser - {@link User} who created the card
    * @param {EvidenceCard} data.evidenceCard1
    * @param {EvidenceCard} data.evidenceCard2
    * @param {object} data.e1creationTime
    * @param {string} data.e1creatorFirstName
    * @param {string} data.e1creatorLastName
    * @param {number} data.e1idEvidenceCard
    * @param {number} data.e1idUser
    * @param {string} data.e1title
    * @param {string} data.e1source
    * @param {string} data.e1citation
    * @param {boolean} data.e1CreatorHasAvatar
    * @param {object} data.e2creationTime
    * @param {object} data.e2creatorFirstName
    * @param {object} data.e2creatorLastName
    * @param {object} data.e2idEvidenceCard
    * @param {object} data.e2idUser
    * @param {object} data.e2title
    * @param {object} data.e2source
    * @param {object} data.e2citation
    * @param {boolean} data.e2CreatorHasAvatar
    * @param {number} data.lane - whether the card is "hot" right now or some other status
    * @param {number} data.points
    * @param {number} data.viewCount
    * @param {boolean} data.canUserSendInvites
    */
   update(data = {}) {
      super.update(data);
      this.idGreySpaceCard = data.idGreySpaceCard;
      this.id = data.idGreySpaceCard || data.id;
      this.lane = data.lane;
      this.points = data.points;
      this.canUserSendInvites = data.canUserSendInvites;
      this.viewCount = data.viewCount;

      if(data.evidenceCard1){
         this.evidenceCard1 = new exports.EvidenceCard(data.evidenceCard1)
      }
      else{
         this.evidenceCard1 = new exports.EvidenceCard({
            creationTime: data.e1creationTime,
            creatorFirstName: data.e1creatorFirstName,
            creatorLastName: data.e1creatorLastName,
            idEvidenceCard: data.e1idEvidenceCard,
            id: data.e1idEvidenceCard,
            idUser: data.e1idUser,
            title: data.e1title,
            source: data.e1source,
            citation: data.e1citation,
            isApproved: true,
            creatorHasAvatar: data.e1CreatorHasAvatar,
         })
      }

      if(data.evidenceCard2){
         this.evidenceCard2 = new exports.EvidenceCard(data.evidenceCard2)
      }
      else{
         this.evidenceCard2 = new exports.EvidenceCard({
            creationTime: data.e2creationTime,
            creatorFirstName: data.e2creatorFirstName,
            creatorLastName: data.e2creatorLastName,
            idEvidenceCard: data.e2idEvidenceCard,
            id: data.e2idEvidenceCard,
            idUser: data.e2idUser,
            title: data.e2title,
            source: data.e2source,
            citation: data.e2citation,
            isApproved: true,
            creatorHasAvatar: data.e2CreatorHasAvatar,
         })
      }

   }

   /**
   * constructor just calls the update function
   * @param {object} data - object that the update function looks through to extract data from
   */
   constructor(data) {
      super(data);
      this.cardType = SharedConstants.CardType.Reasoning;
   }

   hasValidationErrors(){
      let bHasValidationErrors = false;
      if(!this.evidenceCard1.idEvidenceCard || !this.evidenceCard2.idEvidenceCard){
         bHasValidationErrors = true;
      }
      if(!this.title || this.title.trim() === '' ){
         bHasValidationErrors = true;
      }
      return bHasValidationErrors;
   }

   /**
    * Checks to see if the object has all data needed to display properly
    * @return{boolean}
    */
   hasRequiredFields(){
      return (!this.hasValidationErrors() && this.id > 0 && this.idGreySpaceCard > 0 && this.creationTime && this.idUser > 0
                  && this.creator.firstName && this.creator.firstName.trim() !== '' && this.creator.lastName && this.creator.lastName.trim() !== '');

   }
};


/** StimulusCard class */
exports.StimulusCard = class StimulusCard extends CardBase{
   /**
    * Update a StimulusCard (also used in creation)
    * @param {number} data.idStimulusCard
    * @param {number} data.idUser - {@link User} who created the card
    * @param {EvidenceCard[]} data.evidenceCards
    */
   update(data) {
      super.update(data);
      this.idStimulusCard = data.idStimulusCard;
      this.id = data.idStimulusCard;

      this.evidenceCards = [];
      if(data.evidenceCards){
         for(let card of data.evidenceCards){
            this.evidenceCards.push(new exports.EvidenceCard(card));
         }
      }

   }

   /**
   * constructor just calls the update function
   * @param {object} data - object that the update function looks through to extract data from
   */
   constructor(data) {
      super(data);
      this.cardType = SharedConstants.CardType.Stimulus;
   }

   hasValidationErrors(){
      let bHasValidationErrors = false;
      if(!this.title || this.title.trim() === ''){
         bHasValidationErrors = true;
      }
      if(!this.synopsis || this.synopsis.trim() === '' ){
         bHasValidationErrors = true;
      }
      if(this.topics.length <= 0){
         bHasValidationErrors = true;
      }
      return bHasValidationErrors;
   }
};

/** NoteCard class */
exports.NoteCard = class NoteCard extends CardBase{
   /**
    * Update a NoteCard (also used in creation)
    * @param {number} data.idNoteCard
    */
   update(data) {
      super.update(data);
      this.id = data.idNoteCard || data.id;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      super(data);
      this.cardType = SharedConstants.CardType.Note;
   }
};

/** SynthesisCard class */
exports.SynthesisCard = class SynthesisCard extends CardBase{
   /**
    * Update a SynthesisCard (also used in creation)
    * @param {number} data.idSynthesisCardInstance
    */
   update(data) {
      super.update(data);
      this.id = data.idSynthesisCardInstance || data.id;

   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      super(data);
      this.cardType = SharedConstants.CardType.Synthesis;
   }
};

/** QuestionCard class */
exports.QuestionCard = class QuestionCard extends CardBase{
   /**
    * Updates object (also used in creation)
    * @param {number} data.idQuestionCard
    * @param {string} data.answer
    */
   update(data) {
      super.update(data);
      this.id = data.idQuestionCard || data.id;
      this.answer = data.answer;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      super(data);
      this.cardType = SharedConstants.CardType.Question;
   }
};

/** UserVote class */
exports.UserVote = class UserVote{
   /**
    * Update a UserVote (also used in creation)
    * @param {number} data.idGreySpaceCardVote - id of the vote
    * @param {number} data.voteType - value of the vote (1 to 3)
    * @param {number} data.idGreySpaceCard - id of the GreySpaceCard, which is the result of the  link
    */
   update(data) {
      this.idGreySpaceCardVote = data.idGreySpaceCardVote;
      this.voteType = data.voteType;
      this.idGreySpaceCard = data.idGreySpaceCard;
   }

   /**
   * constructor just calls the update function
   * @param {object} data - object that the update function looks through to extract data from
   */
   constructor(data) {
      this.update(data);
  }
};

/** Topic class */
exports.Topic = class Topic{
   /**
    * Update a Topic
    * @param {number} data.idTopic - id of the topic
    * @param {string} data.name - The text associated with the topic
    */
   update(data) {
      this.idTopic = data.idTopic;
      this.id = data.id || data.idTopic;
      this.name = data.name;
      this.isHeader = data.isHeader;
   }

   /**
   * constructor just calls the update function
   * @param {object} data - object that the update function looks through to extract data from
   */
   constructor(data) {
      this.update(data);
  }
};

/** Tag class */
exports.Tag = class Tag{
   /**
    * Update a Tag
    * @param {number} data.idTag - id of the tag
    * @param {string} data.text - The text associated with the tag
    */
   update(data) {
      this.idTag = data.idTag;
      this.text = data.text;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};


/**
 * Helper function to take an array of unformatted topic data and return a new array with the data formatted
 * @param topicArray
 * @returns {Array<Topic>}
 */
exports.formatTopicData = function(topicArray){
   let processed = [];
   for(let topic of topicArray){
      processed.push(new exports.Topic(topic));
   }
   return processed;
};

/** User class */
exports.User = class User{
   /**
    * Update a User
    * @param {number} data.idUser - id of the user
    * @param {string} data.username
    * @param {string} data.firstName
    * @param {string} data.lastName
    * @param {string} data.email
    * @param {number} data.points
    * @param {number[]} data.roles - array of idRole
    * @param {number} data.hasAvatar
    * @param {string} data.bio
    * @param {string} data.location
    * @param {string} data.affiliation
    * @param {number} data.pointsAvailableToBet
    * @param {object[]} data.expertise
    * @param {object[]} data.interest
    * @param {object[]} data.pointAwards
    * @param {object} data.socialNetwork
    * @param {boolean} data.isVerified
    * @param {number} data.level
    * @param {string} data.creationTime
    * @param {string} data.lastActiveTime
    * @param {string} data.linkedinId
    * @param {string} data.orcidId
    * @param {string} data.orcidIdOAuth - not user-editable, this is the orcid ID that comes from the oauth login
    */
   update(data) {
      this.id = data.idUser || data.id;
      this.idUser = data.idUser;
      this.username = data.username;
      this.firstName = data.firstName;
      this.lastName = data.lastName;
      this.email = data.email;
      this.points = data.points;
      this.roles = data.roles;
      this.hasAvatar = data.hasAvatar;
      this.bio = data.bio;
      this.link = data.link;
      this.location = data.location;
      this.affiliation = data.affiliation;
      this.pointsAvailableToBet = data.pointsAvailableToBet;
      this.isVerified = data.isVerified;
      this.level = data.level;
      this.creationTime = data.creationTime;
      this.lastActiveTime = data.lastActiveTime;
      this.achievements = data.achievements || [];
      this.linkedinId = data.linkedinId;
      this.orcidID = data.orcidID;
      this.orcidIdOAuth = data.orcidIdOAuth;

      if (data.expertise) {
         this.expertise = data.expertise;
      }
      else {
         this.expertise = [];
      }

      if (data.interest) {
         this.interest = data.interest;
      }
      else {
         this.interest = [];
      }

      if (data.pointAwards) {
         this.pointAwards = data.pointAwards;
      }
      else {
         this.pointAwards = [];
      }

      if (data.points) {
         this.points = data.points;
      }
      else {
         this.points = 0;
      }

      if (data.socialNetwork) {
         this.socialNetwork = data.socialNetwork;
      }
      else {
         this.socialNetwork = {followers: [], followees: []};
      }
   }

   /**
   * constructor just calls the update function
   * @param {object} data - object that the update function looks through to extract data from
   */
   constructor(data) {
      this.update(data);
   }

   toString() {
      return (this.firstName ? this.firstName + ' ' : '') + (this.lastName || '')
   }
};

/** Deck class */
exports.Deck = class Deck{
   /**
    * Update a deck
    * @param {number} data.idDeck
    * @param {string} data.name - for display purposes only
    * @param {EvidenceCard[]} data.cards
    */
   update(data) {
      this.idDeck = data.idDeck;
      this.name = data.name;
      if(data.cards){
         this.cards = data.cards;
      }
      else{
         this.cards = {};
      }
   }

   /**
   * constructor just calls the update function
   * @param {object} data - object that the update function looks through to extract data from
   */
   constructor(data) {
      this.update(data);
   }
};

/**
 * Helper function to take an array of unformatted deck data and return a new array with the data formatted
 * @param deckArray
 * @returns {Array<Deck>}
 */
exports.formatDeckData = function(deckArray){
   let processed = [];
   for(let deck of deckArray){
      processed.push(new exports.Deck(deck));
   }
   return processed;
};

/** CardComment class */
exports.CardComment = class CardComment{
   /**
    * Update
    * @param {number} data.idThread
    * @param {number} data.idCardComment
    * @param {number} data.idCard
    * @param {number} data.idParentComment
    * @param {number} data.idPortfolioComment
    * @param {Date} data.creationTime
    * @param {string} data.text
    * @param {string} data.title
    * @param {User} data.user
    * @param {number} data.idUser - only used if no user object set
    */
   update(data) {
      this.idThread = data.idThread
      this.idCardComment = data.idCardComment || data.idPortfolioComment;
      this.idCard = data.idCard;
      this.idParentComment = data.idParentComment;
      this.creationTime = data.creationTime;
      this.text = data.text;
      this.title = data.title;
      this.minutesSinceCreation = data.minutesSinceCreation;
      this.idPortfolio = data.idPortfolio;
      this.likes = data.likes || [];
      this.isPinned = data.isPinned || false;
      this.isDisabled = data.isDisabled || false;
      this.linkTitle = data.linkTitle || '';
      this.linkDescription = data.linkDescription || '';
      this.linkImage = data.linkImage || '';
      this.idCard = data.idCard;
      this.cardType = data.cardType;

      if(data.user){
         this.user = data.user;
      }
      else{
         this.user = new exports.User({idUser: data.idUser});
      }
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** PrivateMessage class */
exports.PrivateMessage = class PrivateMessage{
   /**
    * Update a PrivateMessage (also used in creation)
    * @param {number} data.idUserFrom - id of the user who sent the message
    * @param {number} data.idUserTo - id of the recipient user
    * @param {string} data.message - the message sent
    * @param {string} data.title - like an email subject
    * @param {datetime} data.sendDateTime - when the message was sent
    * @param {boolean} data.isUnread - if the message is unread
    */
   update(data) {
      this.id = data.idPrivateMessage || data.id;
      this.idUserFrom = data.idUserFrom;
      this.idUserTo = data.idUserTo;
      this.message = data.message;
      this.title = data.title;
      this.sendTime = data.sendTime;

      if(data.isUnread)
         this.isUnread = data.isUnread;
      else
         this.isUnread = true;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** Portfolio class */
exports.Portfolio = class Portfolio{
   /**
    * Update a Portfolio (also used in creation)
    * @param {number} data.idPortfolio
    * @param {number} data.id
    * @param {string} data.name
    * @param {string} data.description
    * @param {number} data.idOwner
    * @param {number} data.privacyType
    * @param {object[]} data.items
    * @param {object} data.logData
    * @param {object} data.userData
    */
   update(data) {
      this.id = data.idPortfolio || data.id;
      this.idOwner = data.idOwner;
      this.name =  data.name;
      this.description = data.description;
      this.privacyType = data.privacyType;
      this.titleColor = data.titleColor;
      this.bannerColor = data.bannerColor;
      this.zoomMeetingId = data.zoomMeetingId;
      this.zoomMeetingPassword = data.zoomMeetingPassword;

      if(data.items)
         this.items = data.items;
      else
         this.items = [];

      if(data.logData)
         this.logData = data.logData;
      else
         this.logData = [];

      if(data.userData)
         this.userData = data.userData;
      else
         this.userData = [];

      if(data.contributedUsers)
         this.contributedUsers = data.contributedUsers;
      else
         this.contributedUsers = [];
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** Upload class */
exports.Upload = class Upload{
   /**
    * Update object (also used in creation)
    * @param {number} data.id
    * @param {number} data.idIncubatorUpload
    * @param {string} data.url
    * @param {number} data.idIncubator
    * @param {string} data.fileName
    * @param {date} data.uploadTime
    */
   update(data) {
      this.id = data.idIncubatorUpload || data.id;
      this.url = data.url;
      this.idIncubator = data.idIncubator;
      this.fileName = data.fileName;
      this.uploadTime = new Date(data.uploadTime);
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** IncubatorText class */
exports.IncubatorText = class Upload{
   /**
    * Update object (also used in creation)
    * @param {number} data.idIncubatorText
    * @param {number} data.id
    * @param {string} data.text
    * @param {string} data.creationTime
    * @param {number} data.idIncubator
    */
   update(data) {
      this.id = data.idIncubatorText || data.id;
      this.text = data.text;
      this.creationTime = data.creationTime;
      this.idIncubator = data.idIncubator;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** Incubator class */
exports.Incubator = class Incubator extends exports.Portfolio{
   /**
    * Update a Portfolio (also used in creation)
    * @param {Upload[]} data.attachments
    * @param {string[]} data.descriptions
    * @param {string} data.beginTime
    * @param {string} data.displayTime
    * @param {string} data.suggestionEndTime
    * @param {string} data.askMeAnythingTime
    * @param {string} data.abstractDueTime
    * @param {string} data.extendedAbstractDueTime
    * @param {string} data.proposalDueTime
    * @param {string} data.awardTime
    * @param {string} data.descriptionFinalizedTime
    * @param {SharedConstants.PortfolioType} data.privacyType
    * @param {string} data.logoFile
    * @param {string} data.lastUpdateTime
    * @param {User[]} data.owners - Users who can edit this incubator
    * @param {string} data.sponsorDisplayName
    * @param {string} data.instructions
    * @param {string} data.extraCssClass
    */
   update(data) {
      super.update(data);

      this.beginTime = data.beginTime;
      this.displayTime = data.displayTime;
      this.suggestionEndTime = data.suggestionEndTime;
      this.askMeAnythingTime = data.askMeAnythingTime;
      this.abstractDueTime = data.abstractDueTime;
      this.extendedAbstractDueTime = data.extendedAbstractDueTime;
      this.proposalDueTime = data.proposalDueTime;
      this.incubatorEndTime = data.incubatorEndTime;
      this.awardTime = data.awardTime;
      this.descriptionFinalizedTime = data.descriptionFinalizedTime;
      this.privacyType = data.privacyType;
      this.logoFile = data.logoFile;
      this.lastUpdateTime = data.lastUpdateTime;
      this.sponsorDisplayName = data.sponsorDisplayName;
      this.sponsorBio = data.sponsorBio;
      this.extraCssClass = data.extraCssClass || data.extraCSSClass;

      this.isActive = data.isActive;
      this.instructions = data.instructions;
      this.resources = data.resources;
      this.videoLink = data.videoLink;
      this.idBracket = data.idBracket;
      this.discussionType = data.discussionType;
      this.idMania = this.idManiaTournament = data.idMania || data.idManiaTournament;

      if(data.descriptions)
         this.descriptions = data.descriptions;
      else
         this.descriptions = [new exports.IncubatorText({})];

      if(data.attachments)
         this.attachments = data.attachments;
      else
         this.attachments = [];

      if(data.owners)
         this.owners = data.owners;
      else
         this.owners = [];

      if(data.comments)
         this.comments = data.comments;
      else
         this.comments = [];
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      super(data);
   }
};



/** SynthesisTemplate class */
exports.SynthesisTemplate = class SynthesisTemplate{
   /**
    * Update a SynthesisTemplate (also used in creation)
    * @param {number} data.idSynthesisTemplate
    * @param {object} data.title
    * @param {object} data.group
    */
   update(data) {
      this.id = data.idSynthesisCardTemplate || data.id;
      this.title = data.title;

      if(data.group)
         this.group = data.group;
      else
         this.group = {id: data.idGroup};
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** ReviewQuestionTemplate class */
exports.ReviewQuestionTemplate = class ReviewQuestionTemplate{
   /**
    * Updates instance (also used in creation)
    * @param {number} data.idReviewQuestionTemplate
    * @param {object} data.text
    * @param {object} data.voteType
    * @param {string} data.textPrompt
    * @param {number} data.orderNumber
    */
   update(data) {
      this.id = data.idReviewQuestionTemplate || data.id;
      this.text = data.text;
      this.voteType = data.voteType;
      this.textPrompt = data.textPrompt;
      this.orderNumber = data.orderNumber;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/** NewsPost class */
exports.NewsPost = class NewsPost{
   /**
    * Updates instance (also used in creation)
    * @param {number} data.idNewsPost
    * @param {string} data.text
    * @param {string} data.title
    * @param {string} data.creationTime
    */
   update(data) {
      this.id = data.idNewsPost || data.id;
      this.text = data.text;
      this.voteType = data.voteType;
      this.creationTime = data.creationTime;
   }

   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      this.update(data);
   }
};

/**
 * Helper function to take an array of unformatted private message data and return a new array with the data formatted
 * @param PMArray
 * @returns {Array<Achievement>}
 */
exports.formatPrivateMessageData = function(PMArray){
   let processed = [];
   for(let PM of PMArray){
      processed.push(new exports.PrivateMessage(PM));
   }
   return processed;
};

/**
 * Formats an array of all kinds of raw card data into an array of formatted cards
 * @param inputArray
 * @returns {Array<Object>}
 */
exports.formatListViewData = function(inputArray){
   let processed = [];
   for(let obj of inputArray){
      if(obj.id)
         processed.push(exports.formatGenericCard(obj));
   }
   return processed;
};

exports.formatGenericCard = function(cardData){
   if(cardData.cardType === SharedConstants.CardType.Evidence ){
      return (new exports.EvidenceCard(cardData));
   }
   else if(cardData.cardType === SharedConstants.CardType.Reasoning ){
      return (new exports.GreySpaceCard(cardData));
   }
   else if(cardData.cardType === SharedConstants.CardType.Note ){
      return (new exports.NoteCard(cardData));
   }
   else if(cardData.cardType === SharedConstants.CardType.Synthesis ){
      return (new exports.SynthesisCard(cardData));
   }
   else if(cardData.cardType === SharedConstants.CardType.Question ){
      return (new exports.QuestionCard(cardData));
   }
};

//this should probably be a Util function
exports.formatArray = function(dataArray, objectType){
   let processed = [];
   for(let dataRow of dataArray){
      processed.push(new objectType(dataRow));
   }
   return processed;
};




/** UserAdminView class (Ben)*/
exports.UserAdminView = class UserAdminView extends exports.User{
   /**
    * @param {number} data.loginFrequency
    * @param {number} data.avgSessionTime
    * @param {number} data.portfolioCount
    * @param {number} data.evidenceCount
    * @param {number} data.conjectureCount
    * @param {Object[]} data.pageVisitData
    * @param {number} data.evidenceReviewCount
    * @param {number} data.conjectureReviewCount
    */
   update(data) {
      super.update(data);

      this.loginFrequency = data.loginFrequency;
      this.avgSessionTime = data.avgSessionTime;
      this.portfolioCount = data.portfolioCount;
      this.evidenceCount = data.evidenceCount;
      this.conjectureCount = data.conjectureCount;

      if (data.pageVisitData)
         this.pageVisitData = data.pageVisitData;
      else
         this.pageVisitData = [];

      this.evidenceReviewCount = data.evidenceReviewCount;
      this.conjectureReviewCount = data.conjectureReviewCount;


   }
   /**
    * constructor just calls the update function
    * @param {object} data - object that the update function looks through to extract data from
    */
   constructor(data) {
      super(data);
   }
};
